'use client'

import ReactCrop, { type Crop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'

import { showError } from '@/components/atoms/show-error'
import { Button, FileButton, Stack } from '@mantine/core'
import { IconCheck } from '@tabler/icons-react'
import type React from 'react'
import { useRef, useState } from 'react'
import { useServerAction } from 'zsa-react'
import { saveImage } from '../_actions/saveImage'
import {
	centerAspectCrop,
	compressImage,
	getCompressedImageToBase64,
	getPixelCrop,
	isStorageAvailable,
	manageImageCache,
} from '../_utils/imageCompression'

const BUTTON_PROPS = {
	c: 'rgb(168, 105, 170)',
	color: 'rgb(168, 105, 170)',
	radius: 'xl',
	variant: 'outline',
}

export function UploadImage({
	id,
	mode,
	callback,
	setImageUrl,
}: { id: string; mode: 'profile' | 'cover' | 'history'; callback?: () => void; setImageUrl?: (imageUrl: string) => void }) {
	const [crop, setCrop] = useState<Crop>()
	const [image, setImage] = useState<string>()
	const [file, setFile] = useState<File>()
	const [isSuccess, setIsSuccess] = useState(false)
	const imgRef = useRef<HTMLImageElement>(null)

	const { isPending, execute } = useServerAction(saveImage)

	const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
		if (mode === 'cover') {
			setCrop({
				unit: 'px',
				width: 100,
				height: 26,
				x: 0,
				y: 0,
			})
			return
		}

		const { width, height } = e.currentTarget
		// Profile photos use 1:1 aspect ratio (square)
		const aspect = 1
		const crop = centerAspectCrop(width, height, aspect)
		setCrop(crop)
	}

	const handleSuccess = async () => {
		setIsSuccess(true)
		setTimeout(() => {
			callback?.()
			setIsSuccess(false)
			setImage(undefined)
			setFile(undefined)
			setCrop(undefined)
		}, 1000)
	}

	let title = 'Escolher foto para Perfil'
	if (mode === 'cover') {
		title = 'Escolher foto para Capa'
	}
	if (mode === 'history') {
		title = 'Escolher foto para História'
	}

	return (
		<Stack align="center" gap="md">
			<ImageViewer image={image} mode={mode} crop={crop} setCrop={setCrop} imgRef={imgRef} onImageLoad={onImageLoad} />
			<div className="flex gap-2">
				<SaveButton
					handleSuccess={handleSuccess}
					image={image}
					crop={crop}
					isSuccess={isSuccess}
					isPending={isPending}
					file={file}
					imgRef={imgRef}
					execute={execute}
					id={id}
					setImageUrl={setImageUrl}
					mode={mode}
				/>
				<FileButton
					accept="image/png,image/jpeg,image/webp"
					onChange={async file => {
						if (!file) return

						// Compress the image for preview as well
						const compressedFile = await compressImage(file, 300) // Smaller for preview
						setFile(file) // Keep original for processing
						setImage(URL.createObjectURL(compressedFile))
						setIsSuccess(false)
					}}
				>
					{props => (
						<Button loading={isPending} {...props} {...BUTTON_PROPS}>
							{image && crop ? <div>Escolher Outra Foto</div> : <div>{title}</div>}
						</Button>
					)}
				</FileButton>
			</div>
		</Stack>
	)
}

function SaveButton({
	handleSuccess,
	image,
	crop,
	isSuccess,
	isPending,
	file,
	imgRef,
	execute,
	setImageUrl,
	mode,
}: {
	handleSuccess: () => void
	image: string | undefined
	crop: Crop | undefined
	isSuccess: boolean
	isPending: boolean
	file: File | undefined
	imgRef: React.RefObject<HTMLImageElement | null>
	execute: (data: {
		crop: Crop
		file: File
		targetWidth: number
		targetHeight: number
		mode: 'profile' | 'cover' | 'history'
		// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	}) => Promise<[any, any]>
	id: string
	setImageUrl?: (imageUrl: string) => void
	mode: 'profile' | 'cover' | 'history'
}) {
	return image && crop ? (
		<Button
			leftSection={isSuccess ? <IconCheck /> : null}
			disabled={!crop || crop.width === 0 || crop.height === 0}
			loading={isPending}
			{...BUTTON_PROPS}
			onClick={async () => {
				if (!file || !crop || !imgRef.current) return

				// Compress the file before sending
				const compressedFile = await compressImage(file, 800) // Max 800KB

				const pixelCrop = getPixelCrop(imgRef, crop)

				const [result, error] = await execute({
					crop: pixelCrop,
					file: compressedFile, // Send the compressed file directly
					targetWidth: 256,
					targetHeight: 256,
					mode,
				})

				if (result && !error) {
					const url = getCompressedImageToBase64(imgRef, pixelCrop)

					if (isStorageAvailable()) {
						manageImageCache.save({ mode, base64: url })
					}
					setImageUrl?.(url)
					handleSuccess()
				} else {
					showError('Erro ao salvar imagem')
				}
			}}
		>
			{isSuccess ? <span>Salvo com sucesso</span> : <span>Salvar</span>}
		</Button>
	) : null
}

function ImageViewer({
	image,
	crop,
	setCrop,
	imgRef,
	onImageLoad,
	mode,
}: {
	image: string | undefined
	crop: Crop | undefined
	setCrop: (crop: Crop) => void
	imgRef: React.RefObject<HTMLImageElement | null>
	onImageLoad: (e: React.SyntheticEvent<HTMLImageElement>) => void
	mode: 'profile' | 'cover' | 'history'
}) {
	if (!image) return null
	const aspect = 1

	if (mode === 'profile' || mode === 'history') {
		return (
			<ReactCrop crop={crop} onChange={(_, crop) => setCrop(crop)} aspect={aspect} circularCrop>
				<img
					ref={imgRef}
					src={image}
					alt=""
					style={{
						maxWidth: '100%',
						maxHeight: '400px',
					}}
					onLoad={onImageLoad}
				/>
			</ReactCrop>
		)
	}

	if (mode === 'cover') {
		return (
			<ReactCrop crop={crop} onChange={(_, crop) => setCrop(crop)} aspect={791 / 212}>
				<img src={image} ref={imgRef} alt="" style={{ width: '100%', height: '100%' }} onLoad={onImageLoad} />
			</ReactCrop>
		)
	}

	return null
}
